"use client";

/**
 * CodeField — a one-time code, as one cell per digit.
 *
 * Implements the six-cell treatment settled on `/prototypes/one`. A single
 * field asks the reader to count characters to know whether they have
 * finished, and offers nothing to aim at when they need to fix the third
 * digit. Cells answer both.
 *
 * @example
 * const [code, setCode] = useState("");
 *
 * <CodeField
 *   value={code}
 *   onChange={setCode}
 *   length={6}
 *   autoFocus
 *   onComplete={(code) => verify(code)}
 *   describedBy={hintId}
 * />
 *
 * WHAT THE COMPONENT OWNS, so no caller has to remember it:
 *
 *   - Arrows, Home and End move between cells; Backspace on an empty cell
 *     steps back and clears. Six inputs is six focus stops, and a single
 *     field gave that navigation for free.
 *   - Autofill hands the whole code to one input rather than distributing
 *     it, so any multi-character input is treated as a paste — the same path
 *     a real paste takes, which is why it is not a special case.
 *   - A paste fills from whichever cell it lands on, so pasting onto the
 *     fourth box to correct the tail does what it looks like it does.
 *   - Tapping past the first gap lands on the gap. Otherwise the fourth digit
 *     can go in the sixth box and the code looks finished when it is not.
 *     Tapping an *earlier* filled cell is left alone — that is someone fixing
 *     a digit they can see is wrong. Pointer only: pressing an arrow key is a
 *     request to go somewhere, not an aim at a target.
 *   - `aria-label` per cell and a group label, because six unlabelled boxes
 *     are six unlabelled boxes to a screen reader.
 *
 * SHAPE. The cells are square, and that is what makes the corner radius safe:
 * `border-radius` clamps to half the shorter side, and a portrait cell is
 * short across its *width*, so a radius chosen against the height turns into
 * an oval the moment the row is squeezed. Square cells bottom out around
 * 50px, well clear of the clamp at `r-md`. The control radius is the one to
 * avoid — it is a pill in the candidate system, and a pill on a square is a
 * circle. See the same argument about the textarea in the field spec.
 *
 * The caller owns the label, the hint and the resend affordance. This is a
 * field, not a form: the component that knows about cooldowns is the one that
 * knows about sending.
 */

import {
  type ClipboardEvent,
  type KeyboardEvent,
  type PointerEvent,
  useEffect,
  useId,
  useRef,
} from "react";
import { cn } from "@/lib/utils";
import {
  backspaceAt,
  clearAt,
  firstGap as findGap,
  isComplete,
  onlyDigits,
  writeAt,
} from "./code-field-state";

export function CodeField({
  value,
  onChange,
  onComplete,
  id,
  length,
  autoFocus = false,
  invalid = false,
  describedBy,
}: {
  value: string;
  onChange: (next: string) => void;
  /** Fired once the last empty cell is filled, by typing or by paste. */
  onComplete?: (code: string) => void;
  /** Goes on the group, so callers and tests have one stable hook for it. */
  id?: string;
  length: number;
  autoFocus?: boolean;
  /**
   * The code was rejected. Drives `aria-invalid` on every cell, which is also
   * what the styling keys off — so the look and the announced state cannot
   * drift apart. The caller owns the message; a colour is not an explanation.
   */
  invalid?: boolean;
  describedBy?: string;
}) {
  const cells = useRef<(HTMLInputElement | null)[]>([]);
  const groupId = useId();

  useEffect(() => {
    if (autoFocus) cells.current[0]?.focus();
  }, [autoFocus]);

  const focusAt = (i: number) => {
    const el = cells.current[Math.max(0, Math.min(length - 1, i))];
    el?.focus();
    el?.select();
  };

  /** Write `digits` starting at `from`, then park the caret sensibly. */
  const write = (from: number, digits: string) => {
    if (!digits) return;
    const { next, caret } = writeAt(value, length, from, digits);
    onChange(next);
    focusAt(caret);
    // Every cell, not six characters. An interior gap is six characters long.
    if (isComplete(next, length)) onComplete?.(next);
  };

  const handleChange = (i: number, raw: string) => {
    const digits = onlyDigits(raw);
    // Autofill drops the whole code into one cell. Same path as a paste.
    if (digits.length > 1) {
      write(i, digits.slice(0, length - i));
      return;
    }
    if (!digits) {
      // An empty field is a deletion — the browser removed the character.
      // Anything else that survives to here is a rejected keystroke, and a
      // rejected keystroke must not take the digit under it with it: typing
      // a letter over a selected cell was erasing the digit it landed on.
      if (raw === "") onChange(clearAt(value, length, i));
      return;
    }
    write(i, digits);
  };

  const handleKeyDown = (i: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      if (value[i]) return; // let the change handler clear this cell
      e.preventDefault();
      const { next, caret } = backspaceAt(value, length, i);
      onChange(next);
      focusAt(caret);
      return;
    }
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      focusAt(i - 1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      focusAt(i + 1);
    } else if (e.key === "Home") {
      e.preventDefault();
      focusAt(0);
    } else if (e.key === "End") {
      e.preventDefault();
      focusAt(length - 1);
    }
  };

  /**
   * Tapping past the first gap lands on the gap instead.
   *
   * Otherwise it is possible to put the fourth digit in the sixth box and end
   * up with a code that looks finished and is not. Clicking an *earlier*
   * filled cell is left alone — that is someone fixing a digit they can see
   * is wrong, which is the other reason to tap a cell at all.
   *
   * Pointer only. Arrow keys still move freely, because pressing one is a
   * deliberate request to go somewhere rather than an aim at a target.
   */
  const handlePointerDown = (i: number, e: PointerEvent<HTMLInputElement>) => {
    const gap = findGap(value, length);
    if (gap !== -1 && i > gap) {
      e.preventDefault();
      focusAt(gap);
    }
  };

  const handlePaste = (i: number, e: ClipboardEvent<HTMLInputElement>) => {
    const digits = onlyDigits(e.clipboardData.getData("text"));
    if (!digits) return;
    e.preventDefault();
    write(i, digits.slice(0, length - i));
  };

  return (
    <fieldset
      id={id}
      aria-label={`${length}-digit code`}
      aria-describedby={describedBy}
      // `min-w-0` is load-bearing twice over. A fieldset carries a UA
      // `min-inline-size: min-content`, and a flex item's `min-width: auto`
      // beats `max-width` — so six inputs at their intrinsic ~20ch width give
      // a min-content near 1000px, and that forces the whole column wider
      // than its cap instead of overflowing inside it.
      // The gap tightens on a narrow frame so the cells stay as large as the
      // row allows: at this size every point spent between cells is a point
      // off the touch target.
      className="flex w-full min-w-0 justify-center gap-nf-ds-2 border-0 p-nf-ds-0"
    >
      {Array.from({ length }, (_, i) => {
        const char = value[i] ?? "";
        return (
          <input
            // Position is the identity here — the cells never reorder, and a
            // value key would remount the focused input mid-typing.
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length positional cells
            key={i}
            ref={(el) => {
              cells.current[i] = el;
            }}
            id={i === 0 ? groupId : undefined}
            value={char.trim()}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={(e) => handlePaste(i, e)}
            onPointerDown={(e) => handlePointerDown(i, e)}
            onFocus={(e) => e.currentTarget.select()}
            inputMode="numeric"
            // `one-time-code` on the first cell only. Repeating it invites the
            // browser to fill every box with the same full string.
            autoComplete={i === 0 ? "one-time-code" : "off"}
            aria-label={`Digit ${i + 1} of ${length}`}
            aria-invalid={invalid || undefined}
            maxLength={length}
            // One character wide intrinsically. Without this an input asks for
            // roughly 20 characters, which is what makes the row blow out.
            size={1}
            className={cn(
              // Square, so the corner radius reads as a squircle rather than
              // a lozenge. Height used to come from the 48px touch minimum
              // while width was whatever six cells could divide the row into,
              // which makes a portrait box at any narrow width — and a fixed
              // radius on a portrait box is the shape this was trying not to
              // be. Driving both from one axis is the whole fix.
              "aspect-square min-w-0 flex-1 text-center",
              "rounded-nf-ds-md bg-nf-ds-surface-card",
              "font-nf-ds-mono text-nf-ds-xl tabular-nums text-nf-ds-fg @2xl/frame:text-nf-ds-2xl",
              "ring-1 ring-nf-ds-surface-card-ring ring-inset outline-hidden",
              // The caret is the only mark of position inside a cell, so it
              // belongs to the same accent as the focus ring.
              "caret-nf-ds-brand",
              // Filled cells hold a brighter ring, so how far along you are
              // reads without counting characters.
              char.trim() && "ring-nf-ds-fg/25",
              // Rejected. Keyed off the attribute rather than a class, so a
              // cell that looks wrong is always a cell that announces wrong.
              "aria-invalid:ring-2 aria-invalid:ring-nf-ds-destructive",
              "transition-[box-shadow,transform,background-color] duration-150 ease-nf-ds-out",
              // The focused cell lifts. Response on the frame the caret lands,
              // which is the whole point — a ring that fades in reads as lag.
              "focus-visible:scale-105 focus-visible:ring-2 focus-visible:ring-nf-ds-brand",
              // Reduced motion drops the lift, not the whole transition. The
              // ring and fill changes say which cell is live and how far along
              // the code is — that is comprehension, and the preference asks
              // for a gentler equivalent rather than none.
              "motion-reduce:transition-[box-shadow,background-color] motion-reduce:duration-150 motion-reduce:ease-nf-ds-out",
              "motion-reduce:focus-visible:scale-100",
            )}
          />
        );
      })}
    </fieldset>
  );
}
